const UNIVERSAL_LINK_HOSTS = new Set([
    'globethrivers.com',
    'app.globethrivers.com',
    'app-staging.globethrivers.com',
]);
const WRAPPED_URL_PARAM_KEYS = [
    'url',
    'u',
    'target',
    'redirect',
    'redirect_url',
    'redirectUrl',
    'deep_link',
    'deeplink',
    'link',
    'next',
] as const;

const ROUTABLE_DEEP_LINK_PATHS = [
    '/auth/callback',
    '/post/',
    '/profile/',
    '/storefront/',
    '/book/',
    '/redirect',
    '/s/',
    '/itinerary/',
    '/list/',
    '/place/',
    '/recommendation/',
    '/destinations/',
    '/results',
] as const;

const LEGACY_ROUTE_ALIASES: Record<string, string> = {
    '/home': '/',
    '/inspo-to-trip': '/inspo-import',
};

export type NativeDeepLinkTarget =
    | { kind: 'route'; route: string }
    | { kind: 'inspo_import'; prefilledUrl: string };

function pathnameAfterAlias(pathname: string): string {
    const normalized = pathname.startsWith('/') ? pathname : `/${pathname}`;
    return LEGACY_ROUTE_ALIASES[normalized] ?? normalized;
}

function isInspoImportPath(pathname: string): boolean {
    return pathnameAfterAlias(pathname) === '/inspo-import';
}

function isRoutablePath(pathname: string): boolean {
    if (pathname === '/') return true;
    return ROUTABLE_DEEP_LINK_PATHS.some((prefix) =>
        pathname === prefix || pathname.startsWith(prefix),
    );
}

function normalizeRoute(pathname: string, search: string, hash: string): string {
    const mappedPath = LEGACY_ROUTE_ALIASES[pathname] ?? pathname;
    const safePath = mappedPath.startsWith('/') ? mappedPath : `/${mappedPath}`;
    if (safePath.startsWith('//') || safePath.includes('://')) return '/';
    if (!isRoutablePath(safePath)) return '/';
    return `${safePath}${search}${hash}`;
}

function isDirectNativeDeepLinkUrl(rawUrl: string): boolean {
    return (
        rawUrl.startsWith('globethrivers://') ||
        rawUrl.startsWith('https://globethrivers.com') ||
        rawUrl.startsWith('https://app.globethrivers.com') ||
        rawUrl.startsWith('https://app-staging.globethrivers.com')
    );
}

function maybeDecodeCandidate(candidate: string): string {
    const trimmed = candidate.trim();
    if (!trimmed) return trimmed;
    if (/%[0-9A-Fa-f]{2}/.test(trimmed)) {
        try {
            return decodeURIComponent(trimmed);
        } catch {
            return trimmed;
        }
    }
    return trimmed;
}

function extractWrappedUrl(parsed: URL): string | null {
    for (const key of WRAPPED_URL_PARAM_KEYS) {
        const value = parsed.searchParams.get(key);
        if (!value) continue;
        const decoded = maybeDecodeCandidate(value);
        if (decoded) return decoded;
    }
    return null;
}

function unwrapSupportedNativeDeepLink(rawUrl: string, depth = 0): string | null {
    if (!rawUrl || depth > 3) return null;
    if (isDirectNativeDeepLinkUrl(rawUrl)) return rawUrl;

    let parsed: URL;
    try {
        parsed = new URL(rawUrl);
    } catch {
        return null;
    }

    const nested = extractWrappedUrl(parsed);
    if (!nested || nested === rawUrl) return null;
    return unwrapSupportedNativeDeepLink(nested, depth + 1);
}

export function supportsNativeDeepLinkUrl(rawUrl: string): boolean {
    return !!unwrapSupportedNativeDeepLink(rawUrl);
}

/**
 * Resolves a native deep link into either an in-app route or an Inspo Import modal command.
 * Inspo share URLs (`globethrivers://inspo-import?url=…`, legacy `inspo-to-trip`, universal links)
 * no longer map to an SPA route — they open the global modal via context.
 */
export function resolveNativeDeepLinkTarget(rawUrl: string): NativeDeepLinkTarget | null {
    const candidateUrl = unwrapSupportedNativeDeepLink(rawUrl);
    if (!candidateUrl) return null;

    let parsed: URL;
    try {
        parsed = new URL(candidateUrl);
    } catch {
        return null;
    }

    if (parsed.protocol === 'https:' && UNIVERSAL_LINK_HOSTS.has(parsed.hostname)) {
        if (isInspoImportPath(parsed.pathname)) {
            return {
                kind: 'inspo_import',
                prefilledUrl: parsed.searchParams.get('url') ?? '',
            };
        }
        return { kind: 'route', route: normalizeRoute(parsed.pathname, parsed.search, parsed.hash) };
    }

    if (parsed.protocol !== 'globethrivers:') {
        return null;
    }

    if (!parsed.hostname && parsed.searchParams.has('code')) {
        return { kind: 'route', route: `/auth/callback${parsed.search}${parsed.hash}` };
    }

    let routePath = '/';
    if (parsed.hostname && parsed.pathname && parsed.pathname !== '/') {
        routePath = `/${parsed.hostname}${parsed.pathname}`;
    } else if (parsed.hostname) {
        routePath = `/${parsed.hostname}`;
    } else if (parsed.pathname) {
        routePath = parsed.pathname;
    }

    if (isInspoImportPath(routePath)) {
        return {
            kind: 'inspo_import',
            prefilledUrl: parsed.searchParams.get('url') ?? '',
        };
    }

    return { kind: 'route', route: normalizeRoute(routePath, parsed.search, parsed.hash) };
}
